class ProductConfigurator extends DrawerElement {
  constructor() {
    super();

    this.onOptionChangeListener = this.optionChange.bind(this);
    this.onOpenInfoPanel = this.openInfoPanel.bind(this);
    this.onCloseInfoPanel = this.closeInfoPanel.bind(this);
    this.onSubmitForm = this.submitForm.bind(this);
    this.onResetOptions = this.resetOptions.bind(this);
  }

  get cartDrawer() {
    return document.querySelector('cart-drawer');
  }

  connectedCallback() {
    super.connectedCallback();

    this.init();
    this.attachEvents();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.options.forEach(option => option.querySelector('input[type="checkbox"]')?.removeEventListener('change', this.onOptionChangeListener));

    this.infoPanelTriggers.forEach(button => button.removeEventListener('click', this.onOpenInfoPanel));

    this.infoPanelCloseButton.forEach(button => button.removeEventListener('click', this.onCloseInfoPanel));

    this.submitButton.removeEventListener('click', this.onSubmitForm);

    // ✅ Attach Reset button
    this.resetButton = this.querySelector('[data-reset]');
    if (this.resetButton) {
      this.resetButton.addEventListener('click', this.onResetOptions);
    }

    theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.variantChange,
      this.variantChange.bind(this)
    );
  }

  // ✅ Updated resetOptions() to fix ? icon issue
  resetOptions() {
    // Uncheck all checkboxes
    this.querySelectorAll('input[type="checkbox"]').forEach(checkbox => {
      if (checkbox.checked) {
        checkbox.checked = false;
        checkbox.dispatchEvent(new Event("change", { bubbles: true }));
      }
    });

    // Reset radios
    this.querySelectorAll('input[type="radio"]').forEach(radio => {
      radio.checked = false;
      radio.dispatchEvent(new Event("change", { bubbles: true }));
    });

    // Reset swatch sections (labels/images/info only for swatch sections)
    this.querySelectorAll('[data-section]').forEach(section => {
      const labelSelectedValue = section.querySelector('[data-selected-option]');
      const imageTag = section.querySelector('[data-media]');
      const infoBoxTrigger = section.querySelector('[data-target-tab]');
      const hasSwatchOption = section.querySelector('[data-swatch-option]');

      if (labelSelectedValue) labelSelectedValue.textContent = '';
      if (imageTag) imageTag.classList.add('hidden');

      // ✅ Only hide ? icon if this section contains swatch option
      if (infoBoxTrigger && hasSwatchOption) {
        infoBoxTrigger.classList.add('hidden');
      }
    });

    // Reset price back to variant price
    this.updatePrice();
  }

  attachEvents() {
    this.options.forEach(option => option.querySelector('input[type="checkbox"]')?.addEventListener('change', this.onOptionChangeListener));

    this.infoPanelTriggers.forEach(button => button.addEventListener('click', this.onOpenInfoPanel));

    this.infoPanelCloseButton.forEach(button => button.addEventListener('click', this.onCloseInfoPanel));

    this.submitButton.addEventListener('click', this.onSubmitForm);

    theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.variantChange,
      this.variantChange.bind(this)
    );
  }

  hide() {
    super.hide();

    this.closeInfoPanel();
  }

  init() {
    this.variantsOptions = this.querySelector('[data-variants-options]');
    this.sections = this.querySelectorAll('[data-section]');
    this.options = this.querySelectorAll('[data-option]');
    this.infoPanelTriggers = this.querySelectorAll('[data-target-tab]');
    this.infoPanel = this.querySelector('[data-info-panel]');
    this.infoPanelCloseButton = this.querySelectorAll('[data-close-panel]');
    this.submitButton = this.querySelector('[data-submit]');
    this.itemsObject = {};
    this.activeVariant = this.activeVariant ? this.activeVariant : JSON.parse(this.dataset.activeVariant);

    this.exclusionsMap = new Map();
    this.requiredMap = new Map();
    this.collectionMap = new Map();

    this.options.forEach(option => {
      option.collectionUse = new Map();

      const optionExclusionGroups = option.getAttribute('data-exclusion-groups');
      const optionRequiredWith = option.getAttribute('data-required-with');
      const optionCollections = option.getAttribute('data-collections');

      if (optionExclusionGroups) {
        const exclusionGroups = optionExclusionGroups.split(',');

        exclusionGroups.forEach((group) => {
          this.exclusionsMap.set(group, (this.exclusionsMap.get(group) ?? []).concat(option));
        });
      }

      if (optionCollections) {
        const collections = optionCollections.split(',');

        collections.forEach((collection) => {
          this.collectionMap.set(collection, (this.collectionMap.get(collection) ?? []).concat(option));
        });
      }

      if (optionRequiredWith) {
        const requiredWithGroups = optionRequiredWith.split(',');

        requiredWithGroups.forEach((group) => {
          this.requiredMap.set(group, (this.requiredMap.get(group) ?? []).concat(option));

          option.collectionUse.set(group, 0);
        });
      }
    })
  }

  variantChange({ data }) {
    this.activeVariant = data.variant;
    this.toggleOptionsVisibility();
    this.updateButtons();
  }

  updateButtons() {
    const hasVisibilityOption = Array.from(this.options).some(option => !option.classList.contains('hidden'));
    const buttonsWrapper = document.querySelector('[data-buy-buttons]');

    if (!buttonsWrapper) return;

    const configuratorButton = buttonsWrapper.querySelector('[data-configurator-button]');
    const defaultButtons = buttonsWrapper.querySelector('[data-default-buttons]');

    if (!configuratorButton || !defaultButtons) return;

    configuratorButton?.classList?.toggle('hidden', !hasVisibilityOption);
    defaultButtons?.classList?.toggle('hidden', hasVisibilityOption);
  }

  toggleOptionsVisibility() {
    this.options.forEach(option => {
      const checkbox = option.querySelector('input[type="checkbox"]');
      const price = parseInt(option.dataset.price);
      const parentVariantId = option.dataset.parentVariantId;

      option.classList.toggle('hidden', parentVariantId != this.activeVariant.id);

      if (checkbox && parentVariantId != this.activeVariant.id && price != 0) {
        checkbox.checked = false;

        checkbox.dispatchEvent(new Event("change", { bubbles: true }));
      }
    });
  }

  optionChange(event) {
    const target = event.target;
    const option = target.closest('[data-option]');

    this.handleOptionExclusions(target, option);
    this.handleOptionRequired(target, option);
    this.handleOptionExcludes(target, option);

    if (target.closest('[data-swatch-option]')) {
      this.updateSwatchSection(target, option, event);
    }

    this.updatePrice();
  }

  updateSwatchSection(target, option, event) {
    const section = target.closest('[data-section]');

    if (!section) return;

    const imageTag = section.querySelector('[data-media]');
    const labelSelectedValue = section.querySelector('[data-selected-option]');
    const selectedValuePriceContainer = section.querySelector('[data-selected-option-price]');
    const infoBoxTrigger = section.querySelector('[data-target-tab]');

    const isTriggeredByExclusion = event.detail?.triggeredByExclusion || false;

    if (!isTriggeredByExclusion) {
      const activeSwatches = section.querySelectorAll('[data-swatch-option] input[type="checkbox"]:checked');

      activeSwatches?.forEach(checkbox => {
        if (target.checked == true && checkbox !== target && checkbox.checked == true) {
          checkbox.checked = false;
          checkbox.dispatchEvent(new Event("change", { bubbles: true }));
        }
      });
    }

    const activeSwatch = section.querySelector('[data-swatch-option] input[type="checkbox"]:checked');

    if (!activeSwatch) {
      const firstVisibleSwatch = section.querySelector('[data-swatch-option]:not(.hidden)');

      labelSelectedValue.textContent = '';
      imageTag.classList.add('hidden');
      infoBoxTrigger.classList.add('hidden');

      if (firstVisibleSwatch) {
        const price = parseInt(firstVisibleSwatch.dataset.price);

        selectedValuePriceContainer.textContent = `+${theme.Currency.formatMoney(price, theme.settings.currencyCodeEnabled ? theme.settings.moneyWithCurrencyFormat : theme.settings.moneyFormat)}`;
      } else {
        selectedValuePriceContainer.textContent = '';
      }

    } else {
      const parentActiveSwatch = activeSwatch.closest('[data-swatch-option]');
      const price = parseInt(parentActiveSwatch.dataset.price);

      labelSelectedValue.textContent = parentActiveSwatch.dataset.name;
      imageTag.src = parentActiveSwatch.dataset.image;
      imageTag.classList.remove('hidden');

      infoBoxTrigger.dataset.targetTab = parentActiveSwatch.dataset.infoTab;
      infoBoxTrigger.classList.remove('hidden');

      selectedValuePriceContainer.textContent = `+${theme.Currency.formatMoney(price, theme.settings.currencyCodeEnabled ? theme.settings.moneyWithCurrencyFormat : theme.settings.moneyFormat)}`;
    }
  }

  handleOptionExclusions(target, option) {
    const optionExclusionGroups = option.getAttribute('data-exclusion-groups');

    if (!optionExclusionGroups || !target.checked) return;

    const exclusionGroups = optionExclusionGroups.split(',');

    exclusionGroups.forEach(group => {
      const groups = this.exclusionsMap.get(group);

      groups.forEach((groupOption) => {
        if (groupOption !== option) {
          const groupCheckbox = groupOption.querySelector('input[type="checkbox"]');

          if (!groupCheckbox || !groupCheckbox.checked) return;

          groupCheckbox.checked = false;

          groupCheckbox.dispatchEvent(new CustomEvent("change", {
            bubbles: true,
            detail: {
              triggeredByExclusion: true
            }
          }));
        }
      });
    });
  }

  handleOptionRequired(target, option) {
    const optionCollections = option.getAttribute('data-collections');

    if (!optionCollections || this.requiredMap.size === 0) return;

    const collections = optionCollections.split(',');

    collections.forEach(collection => {
      const requiredCollectionGroup = this.requiredMap.get(collection);

      if (requiredCollectionGroup) {
        requiredCollectionGroup.forEach((groupOption) => {
          const delta = target.checked ? 1 : -1;
          groupOption.collectionUse.set(collection, groupOption.collectionUse.get(collection) + delta);

          const requiredCollection = Array.from(groupOption.collectionUse.values()).every(value => value);
          if (requiredCollection) {
            const groupCheckbox = groupOption.querySelector('input[type="checkbox"]');

            this.updateReadOnlyState(groupOption, 1);

            if (groupCheckbox && !groupCheckbox.checked) {
              groupCheckbox.checked = true;
              groupCheckbox.dispatchEvent(new Event("change", { bubbles: true }));
            }
          } else if (!requiredCollection && groupOption.readOnly) {
            this.updateReadOnlyState(groupOption, -1)
          }
        });
      }
    });
  }

  handleOptionExcludes(target, option) {
    const optionExcludesGroup = option.getAttribute('data-excludes');

    if (optionExcludesGroup) {
      const exclusionGroups = optionExcludesGroup.split(',');

      exclusionGroups.forEach(exclusionGroup => {
        const exclusionGroupOptions = this.collectionMap.get(exclusionGroup);

        if (exclusionGroupOptions) {
          exclusionGroupOptions.forEach(exclusionGroupOption => {
            const groupOptionCheckbox = exclusionGroupOption.querySelector('input[type="checkbox"]');

            this.updateReadOnlyState(exclusionGroupOption, target.checked ? 1 : -1);

            if (target.checked && groupOptionCheckbox && groupOptionCheckbox.checked) {
              groupOptionCheckbox.checked = false;

              groupOptionCheckbox.dispatchEvent(new Event("change", { bubbles: true }));
            }
          })
        }
      });
    }
  }

  updateReadOnlyState(option, count) {
    const checkbox = option.querySelector('input[type="checkbox"]');

    if (!checkbox) return;

    if (!('readOnlyCount' in checkbox)) {
      checkbox.readOnlyCount = 0;
    }

    checkbox.readOnlyCount += count;

    if (checkbox.readOnlyCount > 0 && !checkbox.readOnly) {
      checkbox.readOnly = true;
    } else if (checkbox.readOnlyCount < 1 && checkbox.readOnly) {
      checkbox.readOnly = false;
    }
  }

  openInfoPanel(event) {
    const target = event.target;
    const targetTabId = target.dataset.targetTab;
    const targetTab = this.querySelector(`#${targetTabId}`);
    const infoTabs = this.querySelectorAll('[data-info-panel] [data-info-tab]');

    event.preventDefault();

    if (!targetTab) return;

    this.infoPanel.setAttribute('open', true);

    infoTabs.forEach(infoTab => infoTab.classList.toggle('hidden', infoTab !== targetTab));
  }

  closeInfoPanel(event) {
    if (event) {
      event.preventDefault();
    }

    this.infoPanel.removeAttribute('open');
  }

  generateItems() {
    this.itemsObject = {
      items: []
    };

    this.itemsObject.items.push({
      id: this.activeVariant.id,
      quantity: 1,
      properties: {
        _key: this.dataset.parentKey
      }
    });

    const activeOptions = this.querySelectorAll('[data-option]:not(.hidden) input[type="checkbox"]:checked');

    activeOptions.forEach(activeOption => {
      const parent = activeOption.closest('[data-option]');

      this.itemsObject.items.push({
        id: parent.dataset.id,
        quantity: 1,
        properties: {
          _parentKey: parent.dataset.key
        }
      });
    });

    this.itemsObject.items.reverse();
  }

  updatePrice() {
    const activeOptions = this.querySelectorAll('input[type="checkbox"]:checked, input[type="radio"]:checked');
    const totalPrice = Array.from(activeOptions).reduce(
      (accumulator, currentValue) => {
        const parent = currentValue.closest('[data-variant-option]') || currentValue.closest('[data-option]');
        if (!parent || parent.classList.contains('hidden')) return accumulator;

        const price = parseInt(parent.dataset.price);
        return accumulator + price;
      }, this.activeVariant.price);

    const moneyFormat = theme.settings.currencyCodeEnabled ? theme.settings.moneyWithCurrencyFormat : theme.settings.moneyFormat;

    this.submitButton.querySelector('.btn-text').textContent = this.submitButton.dataset.textPattern.replace(
      '[money]',
      theme.Currency.formatMoney(totalPrice, moneyFormat)
    );
  }

  submitForm() {
    this.generateItems();

    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } }));

    this.submitButton.setAttribute('aria-disabled', 'true');
    this.submitButton.setAttribute('aria-busy', 'true');

    const body = JSON.stringify({
      ...this.itemsObject,
      sections: sectionsToBundle,
      sections_url: window.location.pathname
    });

    this.handleErrorMessage();

    fetch(`${theme.routes.cart_add_url}`, { ...theme.utils.fetchConfig('javascript'), body })
      .then((response) => response.json())
      .then(async (parsedState) => {
        if (parsedState.status) {
          this.handleErrorMessage(parsedState.description);

          document.dispatchEvent(new CustomEvent('ajaxProduct:error', {
            detail: {
              errorMessage: parsedState.description
            }
          }));
          return;
        }

        const cartJson = await (await fetch(theme.routes.cart_url, { ...theme.utils.fetchConfig('json', 'GET') })).json();
        cartJson['sections'] = parsedState['sections'];

        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'product-configurator', cart: cartJson });

        document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
          detail: {
            product: parsedState
          }
        }));

        this.hide();

        this.cartDrawer?.show();
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        this.submitButton.removeAttribute('aria-busy');
      });
  }

  handleErrorMessage(errorMessage = false) {
    this.errorMessage = this.querySelector('[data-error-message]');
    if (!this.errorMessage) return;

    this.errorMessage.toggleAttribute('hidden', !errorMessage);
    this.errorMessage.innerText = errorMessage;
  }
}

customElements.define('product-configurator', ProductConfigurator);
